import { BaseComponent } from '../../lib/BaseComponent.js';
import { isOnline, registerNetworkListeners, getUnsyncedCount } from '../../lib/offline-storage.js';

/**
 * Offline Indicator Component
 * Shows a banner when the user is offline or has unsynced data
 */
export class OfflineIndicator extends BaseComponent {
  private isOffline = !isOnline();
  private unsyncedCount = 0;
  private cleanupListeners: (() => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();

    // Check unsynced count on load
    this.checkUnsyncedCount();

    // Register network listeners
    this.cleanupListeners = registerNetworkListeners(
      () => this.handleOnline(),
      () => this.handleOffline()
    );

    // Check unsynced count periodically
    setInterval(() => this.checkUnsyncedCount(), 30000); // Every 30 seconds
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.cleanupListeners) {
      this.cleanupListeners();
    }
  }

  private async handleOnline(): Promise<void> {
    this.isOffline = false;
    await this.checkUnsyncedCount();
    this.shadow.innerHTML = '';
    await this.render();

    // Try to sync if there are unsynced items
    if (this.unsyncedCount > 0) {
      this.triggerSync();
    }
  }

  private async handleOffline(): Promise<void> {
    this.isOffline = true;
    await this.checkUnsyncedCount();
    this.shadow.innerHTML = '';
    await this.render();
  }

  private async checkUnsyncedCount(): Promise<void> {
    try {
      this.unsyncedCount = await getUnsyncedCount();
      if (this.shadow.innerHTML) {
        this.shadow.innerHTML = '';
        await this.render();
      }
    } catch (error) {
      console.error('Failed to check unsynced count:', error);
    }
  }

  private triggerSync(): void {
    // Trigger background sync if available
    if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
      navigator.serviceWorker.ready.then((registration) => {
        return (registration as any).sync.register('sync-inspections');
      }).then(() => {
        console.log('Background sync registered');
      }).catch((error) => {
        console.error('Background sync registration failed:', error);
      });
    }
  }

  protected async render(): Promise<void> {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    // Don't render anything if online and no unsynced data
    if (!this.isOffline && this.unsyncedCount === 0) {
      return;
    }

    const container = this.createElement('div', { className: 'offline-indicator' });

    if (this.isOffline) {
      // Offline banner
      const offlineBanner = this.createElement('div', { className: 'banner offline-banner' });
      const icon = this.createElement('span', { className: 'banner-icon' }, '📡');
      const text = this.createElement('span', { className: 'banner-text' }, 'You are offline');
      const subtitle = this.createElement('span', { className: 'banner-subtitle' }, 'Some features may be limited');

      offlineBanner.appendChild(icon);
      offlineBanner.appendChild(text);
      offlineBanner.appendChild(subtitle);
      container.appendChild(offlineBanner);
    }

    if (this.unsyncedCount > 0) {
      // Unsynced data banner
      const unsyncedBanner = this.createElement('div', { className: 'banner unsynced-banner' });
      const icon = this.createElement('span', { className: 'banner-icon' }, '🔄');
      const text = this.createElement('span', { className: 'banner-text' }, `${this.unsyncedCount} item${this.unsyncedCount > 1 ? 's' : ''} pending sync`);
      const subtitle = this.createElement('span', { className: 'banner-subtitle' }, this.isOffline ? 'Will sync when back online' : 'Syncing...');

      unsyncedBanner.appendChild(icon);
      unsyncedBanner.appendChild(text);
      unsyncedBanner.appendChild(subtitle);

      // Add sync button if online
      if (!this.isOffline) {
        const syncBtn = this.createElement('button', { className: 'sync-btn' }, 'Sync Now');
        syncBtn.addEventListener('click', () => this.triggerSync());
        unsyncedBanner.appendChild(syncBtn);
      }

      container.appendChild(unsyncedBanner);
    }

    this.shadow.appendChild(container);
  }

  private getStyles(): string {
    return `
      .offline-indicator {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        gap: 0;
      }

      .banner {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        padding: var(--spacing-md) var(--spacing-lg);
        color: white;
        font-size: 14px;
        font-weight: 500;
        animation: slideDown 0.3s ease-out;
      }

      @keyframes slideDown {
        from {
          transform: translateY(-100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .offline-banner {
        background: #dc2626;
        box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
      }

      .unsynced-banner {
        background: #f59e0b;
        box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
      }

      .banner-icon {
        font-size: 20px;
        animation: pulse 2s ease-in-out infinite;
      }

      @keyframes pulse {
        0%, 100% {
          opacity: 1;
          transform: scale(1);
        }
        50% {
          opacity: 0.7;
          transform: scale(0.95);
        }
      }

      .banner-text {
        flex: 1;
        font-weight: 600;
      }

      .banner-subtitle {
        font-size: 12px;
        opacity: 0.9;
        margin-left: auto;
      }

      .sync-btn {
        padding: 6px 12px;
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 4px;
        color: white;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        margin-left: var(--spacing-md);
      }

      .sync-btn:hover {
        background: rgba(255, 255, 255, 0.3);
        border-color: rgba(255, 255, 255, 0.5);
      }

      @media (max-width: 768px) {
        .banner {
          font-size: 13px;
          padding: var(--spacing-sm) var(--spacing-md);
          flex-wrap: wrap;
        }

        .banner-subtitle {
          flex-basis: 100%;
          margin-left: 32px;
          margin-top: 4px;
        }

        .sync-btn {
          margin-left: auto;
        }
      }
    `;
  }
}

// Register custom element
customElements.define('offline-indicator', OfflineIndicator);
